/**
 * src/domain/identity/sovereign-score.ts
 *
 * Public 0–300 Sovereign Score Calculator.
 * Measures cryptographic verification, content quality, vouching graph trust,
 * DePIN PoPR relay contributions, community jury participation, and staking longevity.
 */

export type VerificationTier = 'none' | 'email' | 'passkey' | 'zk_id' | 'vouched_3x';

export interface SovereignScoreProfile {
  verificationTier: VerificationTier;
  avgEndorsementScore: number;  // average community endorsement (0 to 100)
  incomingVouches: number;       // number of peer vouches received
  totalBytesRelayed: number;     // DePIN bandwidth relayed in bytes
  juryVotes: number;             // community jury consensus participation
  daysStaked: number;            // days tokens or bond have remained staked
}

export interface SovereignScoreBreakdown {
  totalScore: number;            // capped at 300
  tierPoints: number;            // max 100
  qualityPoints: number;         // max 50
  vouchPoints: number;           // max 50
  poprPoints: number;            // max 50
  juryPoints: number;            // max 20
  stakePoints: number;           // max 30
  rankTitle: SovereignRankTitle;
  badgeFlair: string;
}

export type SovereignRankTitle =
  | 'Unverified Wanderer'
  | 'Mesh Citizen'
  | 'Trusted Sentinel'
  | 'Guardian of the Mesh'
  | 'Sovereign Legend';

export const VERIFICATION_TIER_POINTS: Record<VerificationTier, number> = {
  none: 0,
  email: 10,
  passkey: 20,
  zk_id: 50,
  vouched_3x: 100,
};

/**
 * Calculates the Sovereign Score with strict caps per sub-metric.
 */
export function calculateSovereignScore(profile: SovereignScoreProfile): SovereignScoreBreakdown {
  const tierPoints = VERIFICATION_TIER_POINTS[profile.verificationTier] ?? 0;
  const qualityPoints = Math.min(Math.max(0, profile.avgEndorsementScore) * 0.5, 50);
  const vouchPoints = Math.min(Math.max(0, profile.incomingVouches) * 5, 50);
  const poprPoints = Math.min(Math.max(0, profile.totalBytesRelayed) / 1e9 * 50, 50); // 50 pts per 1 GB
  const juryPoints = Math.min(Math.max(0, profile.juryVotes) * 2, 20);
  const stakePoints = Math.min(Math.max(0, profile.daysStaked) * 0.1, 30);

  const rawSum = tierPoints + qualityPoints + vouchPoints + poprPoints + juryPoints + stakePoints;
  const totalScore = Math.min(Math.round(rawSum), 300);

  let rankTitle: SovereignRankTitle = 'Unverified Wanderer';
  let badgeFlair = '🌱';

  if (totalScore >= 250) {
    rankTitle = 'Sovereign Legend';
    badgeFlair = '💎';
  } else if (totalScore >= 180) {
    rankTitle = 'Guardian of the Mesh';
    badgeFlair = '🛡️';
  } else if (totalScore >= 100) {
    rankTitle = 'Trusted Sentinel';
    badgeFlair = '⚡';
  } else if (totalScore >= 30) {
    rankTitle = 'Mesh Citizen';
    badgeFlair = '🏛️';
  }

  return {
    totalScore,
    tierPoints: Math.round(tierPoints),
    qualityPoints: Math.round(qualityPoints),
    vouchPoints: Math.round(vouchPoints),
    poprPoints: Math.round(poprPoints),
    juryPoints: Math.round(juryPoints),
    stakePoints: Math.round(stakePoints),
    rankTitle,
    badgeFlair,
  };
}
